/* -------------------------------------------------------------------------- */
/* Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Lib dependencies
import {
	ActionIcon,
	Drawer,
	Flex,
	NumberFormatter,
	RangeSlider,
	Stack,
	Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

// Styles
import classes from './Filter.module.css';

// UI local components
import { Button, Title } from '@components/v2/ui';

// Icons
import { IconX } from '@tabler/icons-react';

// Utils
import { MultiSelect } from '@components/v2/ui/MultiSelect/MultiSelect';
import { getCurrencyUnit } from '@utils/currency-units';
import {
	cloneDeep,
	convertArrayToLabelValue,
} from '@utils/functions';

// Apis
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { TSelectOption } from '@global-types/app/app';
import { useAuthorization } from '@lib/authorization';
import useStore from '@store/index';
import { useAdditionalities } from 'shared/api/get-additionalities';
import { useBrands } from 'shared/api/get-brands';

/* -------------------------------------------------------------------------- */
/* Types                                     */
/* -------------------------------------------------------------------------- */

export type RangeFilter = {
	$gte: number;
	$lte: number;
};

export type QueryParams = {
	entity?: string[];
	[key: string]: unknown;
	createdAt?: {
		$gte: string | Date;
		$lt: string | Date;
	};
	'financing.pledged_euro'?: RangeFilter;
};

type TFilterOffsetsProps = {
	opened: boolean;
	onClose: () => void;
	title: string;
	onApplyFilters: (filters: QueryParams) => void;
	value: QueryParams;
};

/* -------------------------------------------------------------------------- */
/* Component                                 */
/* -------------------------------------------------------------------------- */

export const Filter = ({
	opened,
	onClose,
	title,
	onApplyFilters,
	value,
}: TFilterOffsetsProps) => {
	/* ******************************** HOOKS ******************************* */

	const { t } = useTranslation();

	const { data: brands } = useBrands();

	const { checkRole } = useAuthorization();

	const {
		setOffsetAdditionalProjectsPage,
		setOffsetAdditionalProjectsQueryParams,
	} = useStore();

	const { data: projects } = useAdditionalities();

	const [queryParams, setQueryParams] =
		useState<QueryParams>(value ?? {});

	const [fundedPartSlider, setFundedPartSlider] =
		useState<[number, number]>([0, 100000]);

	/* ******************************** HELPERS ******************************* */

	const handleReset = () => {
		setOffsetAdditionalProjectsQueryParams({});
		setFundedPartSlider([0, 100000]);
		setQueryParams({});
		onApplyFilters({});
		onClose();
	};

	const handleApplyFilters = () => {
		setOffsetAdditionalProjectsQueryParams(queryParams);
		setOffsetAdditionalProjectsPage(1);
		onApplyFilters(queryParams);
		onClose();
	};

	useEffect(() => {
		if (opened) {
			setQueryParams(cloneDeep(value ?? {}));

			const fundedFilter =
				value?.['financing.pledged_euro'];
			setFundedPartSlider([
				fundedFilter?.$gte ?? 0,
				fundedFilter?.$lte == 1000000000000000
					? 100000
					: (fundedFilter?.$lte ?? 100000),
			]);
		}
	}, [opened, value]);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer
			classNames={classes}
			opened={opened}
			position="right"
			onClose={onClose}
			withCloseButton={false}
			size="544px"
		>
			<Flex
				align="center"
				gap="16px"
				justify="space-between"
				pb={'var(--mantine-spacing-xl)'}
			>
				<Title
					order={6}
					c="var(--mantine-color-igluPrimary-9)"
				>
					{title}
				</Title>
				<ActionIcon
					color="var(--mantine-color-igluBorder-9)"
					radius={'48px'}
					variant="outline"
					size={'48px'}
					onClick={onClose}
				>
					<IconX
						color="var(--mantine-color-igluPrimary-9)"
						size={'24px'}
					/>
				</ActionIcon>
			</Flex>

			<Flex
				flex={1}
				direction={'column'}
				justify="space-between"
			>
				<Stack gap={'var(--mantine-spacing-sm)'}>
					<MultiSelect
						placeholder={t(
							'filters.placeholders.selectProject'
						)}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={convertArrayToLabelValue(projects)}
						clearable
						size="lg"
						value={queryParams._id ? queryParams._id : []}
						searchable
						onChange={(values) => {
							const selected = values
								.map((val) => {
									const tech = convertArrayToLabelValue(
										projects
									).find((t) => t.value === val);
									return tech
										? {
												label: tech.label,
												value: tech.value,
											}
										: null;
								})
								.filter(Boolean);

							setQueryParams({
								...queryParams,
								_id: selected.map(
									(x: TSelectOption) => x.value
								),
							});
						}}
					/>

					<MultiSelect
						placeholder={t(
							'filters.placeholders.selectTechnology'
						)}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={PROJECT_TECHNOLOGIES}
						clearable
						size="lg"
						value={queryParams.technology ?? []}
						searchable
						onChange={(values) => {
							const selected = values
								.map((val) => {
									const tech = PROJECT_TECHNOLOGIES.find(
										(t) => t.value === val
									);
									return tech
										? {
												label: tech.label,
												value: tech.value,
											}
										: null;
								})
								.filter(Boolean);

							setQueryParams({
								...queryParams,
								technology: selected.map(
									(x: TSelectOption) => x.value
								),
							});
						}}
					/>

					<MultiSelect
						placeholder={t(
							'filters.placeholders.selectCountry'
						)}
						w={'100%'}
						comboboxProps={{ withinPortal: false }}
						data={EUROPEAN_COUNTRIES}
						clearable
						size="lg"
						value={
							queryParams['location.countryCode']
								? queryParams['location.countryCode'].$in
								: []
						}
						searchable
						onChange={(values) => {
							setQueryParams({
								...queryParams,
								'location.countryCode': { $in: values },
							});
						}}
					/>

					<Stack mt="md">
						<Text
							c="var(--mantine-color-igluTextSecondary-9)"
							variant="button"
							mb={'8px'}
						>
							{t('global.fundedPart')}
						</Text>

						<RangeSlider
							labelAlwaysOn
							label={(value) =>
								value === 0 || value === 100000 ? null : (
									<NumberFormatter
										thousandSeparator=" "
										value={value}
										suffix={' ' + getCurrencyUnit('EUR')}
										decimalScale={2}
									/>
								)
							}
							marks={[
								{ value: 0, label: '0€' },
								{ value: 100000, label: '100k€' },
							]}
							color="var(--mantine-color-igluPrimary-9)"
							value={fundedPartSlider}
							min={0}
							max={100000}
							step={1}
							onChange={(value) => {
								setFundedPartSlider(value);
								if (
									value[0] === 0 &&
									value[1] === 100000
								) {
									const clone = cloneDeep(queryParams);
									delete clone['financing.pledged_euro'];
									setQueryParams(clone);
								} else {
									setQueryParams({
										...queryParams,
										'financing.pledged_euro': {
											$gte: value[0],
											$lte:
												value[1] === 100000
													? 1000000000000000
													: value[1],
										},
									});
								}
							}}
						/>
					</Stack>
				</Stack>

				<Flex
					direction={{
						base: 'column-reverse',
						sm: 'row',
					}}
					gap={{
						base: 'var(--mantine-spacing-sm)',
						sm: 'var(--mantine-spacing-lg)',
					}}
					mt="xl"
				>
					<Button
						fullWidth
						variant={'outline'}
						onClick={handleReset}
					>
						{t('global.resetFilters')}
					</Button>
					<Button
						onClick={handleApplyFilters}
						fullWidth
						variant={'primary'}
					>
						{t('global.applyFilters')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
